import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Riporta sulle schede dei cantieri le persone e i mezzi che stanno gia' in
 * pianificazione.
 *
 * Serve per le assegnazioni fatte prima che la griglia allineasse la scheda da
 * sola. Si guarda sempre prima di scrivere: il conto rifa' l'elenco della
 * scheda partendo dalla pianificazione, quindi una persona messa a mano sulla
 * scheda e mai comparsa in nessuna giornata sparirebbe, e va detto prima.
 */
export default function RiallineaSchede({ dal, al, onDone }) {
  const [aperto, setAperto] = useState(false);
  const [da, setDa] = useState(dal);
  const [a, setA] = useState(al);
  const [esito, setEsito] = useState(null);
  const [applicato, setApplicato] = useState(false);
  const [busy, setBusy] = useState(false);

  const chiudi = () => { setAperto(false); setEsito(null); setApplicato(false); };

  const esegui = async (soloVerifica) => {
    setBusy(true);
    try {
      const { data } = await api.post("/planning/riallinea-schede",
        { date_from: da, date_to: a, solo_verifica: soloVerifica });
      setEsito(data);
      setApplicato(!soloVerifica);
      if (!soloVerifica) {
        toast.success(`${data.da_cambiare} schede aggiornate`);
        onDone?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (<>
    <Button variant="outline" size="sm" onClick={() => setAperto(true)}
            data-testid="riallinea-schede-btn">
      <RefreshCw className="w-4 h-4 mr-2" /> Allinea schede cantieri
    </Button>

    {aperto && (
      <Dialog open onOpenChange={chiudi}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Allinea le schede dei cantieri</DialogTitle></DialogHeader>

          <div className="space-y-4">
            <p className="text-xs text-slate-600">
              Riporta sulle schede dei cantieri le persone e i mezzi che stanno già in
              pianificazione. Serve per le assegnazioni fatte prima che la griglia
              allineasse la scheda da sola.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cantieri pianificati dal</Label>
                <Input type="date" className="h-10 mt-1" value={da}
                       onChange={(e) => { setDa(e.target.value); setEsito(null); }} />
              </div>
              <div>
                <Label className="text-xs">Al</Label>
                <Input type="date" className="h-10 mt-1" value={a}
                       onChange={(e) => { setA(e.target.value); setEsito(null); }} />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Il periodo dice <b>quali cantieri</b> guardare. Per ognuno il conto viene
              rifatto su tutte le sue giornate, anche quelle fuori da queste date.
            </p>

            {esito && (
              <div className="space-y-3">
                <div className={`text-sm rounded p-3 border ${applicato
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-blue-50 border-blue-200 text-blue-800"}`}>
                  {applicato
                    ? `${esito.da_cambiare} schede aggiornate.`
                    : esito.da_cambiare === 0
                      ? "Nessuna scheda da aggiornare: sono già tutte allineate."
                      : `${esito.da_cambiare} schede da aggiornare su ${esito.cantieri_nel_periodo} cantieri del periodo. Niente è stato ancora salvato.`}
                </div>

                {!applicato && esito.con_rimozioni > 0 && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      Su <b>{esito.con_rimozioni}</b> cantieri qualcuno sparirebbe dalla
                      scheda: sono persone o mezzi messi a mano che non compaiono in
                      nessuna giornata di pianificazione. Controlla la colonna
                      &ldquo;perse&rdquo; qui sotto prima di confermare.
                    </div>
                  </div>
                )}

                {esito.cantieri.length > 0 && (
                  <div className="border border-slate-200 rounded overflow-x-auto max-h-80 overflow-y-auto">
                    <table className="w-full text-sm min-w-[560px]">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500 sticky top-0">
                        <tr>
                          <th className="text-left p-2">Cantiere</th>
                          <th className="text-center p-2">Persone</th>
                          <th className="text-center p-2">Mezzi</th>
                          <th className="text-right p-2">Perse</th>
                        </tr>
                      </thead>
                      <tbody>
                        {esito.cantieri.map(c => (
                          <tr key={c.site_id} className={`border-t border-slate-100 ${
                            c.persone_rimosse || c.mezzi_rimossi ? "bg-amber-50" : ""}`}>
                            <td className="p-2">
                              <div className="font-medium">{c.cantiere}</div>
                              <div className="text-[10px] text-slate-400">
                                {c.committente || "—"} · {c.giornate} giornate
                              </div>
                            </td>
                            <td className="p-2 text-center whitespace-nowrap">
                              <span className="text-slate-400">{c.persone_prima}</span>
                              <ArrowRight className="w-3 h-3 inline mx-1 text-slate-400" />
                              <span className="font-semibold">{c.persone_dopo}</span>
                            </td>
                            <td className="p-2 text-center whitespace-nowrap">
                              <span className="text-slate-400">{c.mezzi_prima}</span>
                              <ArrowRight className="w-3 h-3 inline mx-1 text-slate-400" />
                              <span className="font-semibold">{c.mezzi_dopo}</span>
                            </td>
                            <td className="p-2 text-right">
                              {c.persone_rimosse || c.mezzi_rimossi
                                ? <span className="text-amber-800 font-semibold">
                                    {c.persone_rimosse + c.mezzi_rimossi}
                                  </span>
                                : <span className="text-slate-300">—</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={chiudi}>Chiudi</Button>
              {!applicato && (
                <Button variant="outline" disabled={busy} onClick={() => esegui(true)}>
                  {busy ? "Controllo..." : "Guarda cosa cambia"}
                </Button>
              )}
              {!applicato && esito && esito.da_cambiare > 0 && (
                <Button disabled={busy} onClick={() => esegui(false)}
                        className="bg-orange-500 hover:bg-orange-600 text-white">
                  {busy ? "Salvataggio..." : `Allinea ${esito.da_cambiare} schede`}
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    )}
  </>);
}
